// src/components/ClaimFormPanel.js
//
// SECTION 512 (2026-09-29). THE PENALTY TIME CLAIM ON THE CLAIM PAGE - rulings 256, 257, 259, 260.
// Bob, 2026-09-29, reading claim 0001's page: "are these the actual claims?" - they were not; the page showed the
// old initial-notice letter group (ruling 130) as plain text. Bob: "go" on putting his Penalty Time Claim form on
// every claim page for the claimant and her officers, and hiding the old letter group.
//
// What this shows is EXACTLY the PDF src\data\penaltyClaimPdf.js writes - the same bytes download and print. Until
// every required item is on the record the PDF carries "PREVIEW - NOT READY TO SEND" and the list of what is missing
// is shown above it. Nothing here writes to the claim. The signature is never affixed (237/238): the signer signs
// the printed or downloaded copy himself.
import React, { useEffect, useMemo, useState } from 'react';
import DocumentReview from '../components/DocumentReview';   // section 543: spelling, grammar, facts, prose
import { penaltyClaimText, factsFromClaim as claimFacts, claimRecordLine } from '../data/documentReview';   // section 543
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { claimFormModel, formRoleOf, formRolesFor, FORM_ROLES, claimFormFileName } from '../data/penaltyClaim';   // ruling 271: one builder, shared with the filer's copy at filing
import { penaltyClaimPdf } from '../data/penaltyClaimPdf';
import { SlidesButton } from './SlideDeck';   // ruling 311: presenting the claim as slides
import { claimDeck } from '../data/slideDecks';
import { resolveRuleText } from '../data/ruleResolver';
import { claimCommittee } from '../data/claimPayload';
import { getClaimRequirements } from '../data/claimRequirements';   // ruling 262: the officer the agreement designates
import { penaltyClaimDocx } from '../data/penaltyClaimDocx';   // section 517: the same form as a Word file
import ClaimFormActions from './ClaimFormActions';   // section 517: approve the statement, give the address, decline to sign
import { doc as fdoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth } from '../firebase';
import { claimPatch } from '../data/claimWrite';
import { completionRows, completionPatch } from '../data/claimCompletion'; import { worksInPrivate } from '../data/workingCopy'; import { saveCompletionToCopy } from '../data/workingCopyStore';   // section 537: fill in what's missing; Record 647 (ruling 305.10): an officer's values go to his working copy
import { SUPPORTING_AWARDS, SUPPORTING_AWARDS_URL, SUPPORTING_AWARDS_NOTE } from '../data/penaltyClaim';   // section 537
import PresentedToCheckPanel from './PresentedToCheckPanel';   // Record 610 (ruling 262): "Presented to" checked three ways
import { crossCheck, entryFor } from '../data/presentedToCheck';   // Record 610 (ruling 262)
import { officeAddressFor } from '../data/contactDetails';   // Record 610 (ruling 259): the seat's office address
import { loadDirectories } from '../data/directoryLoad';   // Record 610: the two directories and the letterhead, one loader
import { serviceFor, agreementSlug } from '../data/serviceSettings';   // section 539: the GC's setting per property
import { wordsFor } from '../data/craftTerms';   // section 807: the words of the claim's division
import { getFunctions, httpsCallable } from 'firebase/functions';   // section 761 (ruling 325)

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', ok: '#5FB878', text: '#DCE7F2', bad: '#E06C6C' };
const s = (v) => (v == null ? '' : String(v).trim());
const LOGO = { path: '/smart-logo-2017.jpg', w: 200, h: 33 };   // from Bob's 2017-08-11 SMART-TD office letter

// Ruling 271 (2026-10-02): moved to penaltyClaim.js (formRoleOf) so the filer's copy at filing uses the same rule.
const roleOf = (user) => formRoleOf(user);

// SECTION 537: "Why aren't these filled in?" - the officer who may work the claim fills in its blanks here. The rules
// decide who may write (lcEdit / gcEdit / adminEdit); a refusal is shown as it comes, and nothing changes.
function CompletionPanel({ claim, claimId, user, missing, onPatched }) {
  const rows = completionRows(claim, missing);
  const [open, setOpen] = useState(rows.some(r => r.isMissing));
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const save = async () => {
    const r = completionPatch(claim, vals);
    if (!r.ok) { setMsg(r.errors.join(' ')); return; }
    if (!r.changed) { setMsg('Nothing changed.'); return; }
    setBusy(true); setMsg('');
    try {
      if (worksInPrivate(user, claim)) { const w = await saveCompletionToCopy(claimId, user, vals); if (!w.ok) { setMsg('Not saved: ' + w.errors.join(' ')); setBusy(false); return; } setVals({}); setMsg('Saved to your working copy - nobody else sees it until you press Publish to the claim.'); setBusy(false); return; }   // Record 647
      await updateDoc(fdoc(db, 'claims', claimId), claimPatch({ ...r.patch, updatedAt: serverTimestamp() }, user || auth.currentUser)); setVals({}); setMsg('Saved.');
      if (onPatched) onPatched(r.patch);
    } catch (e) { setMsg('Not saved: ' + ((e && (e.code || e.message)) || 'refused') + '. Nothing was changed.'); }
    setBusy(false);
  };
  const n = rows.filter(r => r.isMissing).length;
  return (
    <div style={{ border: '1px solid ' + (n ? C.accent : C.line), padding: '10px 12px', marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Fill in what's missing {n ? <span style={{ color: C.accent, fontWeight: 400 }}>- {n} blank on the form</span> : null}</div>
        <button className="btn btn-secondary btn-sm" onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            {rows.map(r => (
              <label key={r.key} style={{ fontSize: 11, color: r.isMissing ? C.accent : C.dim }}>
                {r.label}{r.isMissing ? ' - blank' : ''}
                <input aria-label={r.label || r.key} className="form-input" type={r.type === 'number' ? 'text' : r.type} inputMode={r.type === 'number' ? 'decimal' : undefined} spellCheck={r.type === 'text'}
                  value={r.key in vals ? vals[r.key] : r.value} onChange={e => setVals(v => ({ ...v, [r.key]: e.target.value }))} style={{ fontSize: 13, marginTop: 2 }} />
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" disabled={busy || !Object.keys(vals).length} onClick={save}>{busy ? 'Saving…' : worksInPrivate(user, claim) ? 'Save to my working copy' : 'Save to the claim'}</button>
            <span style={{ fontSize: 11, color: /^Not saved/.test(msg) ? C.bad : C.dim }}>{msg || 'The Submission Date is the day the claim is served on the Carrier - it fills in when service is recorded.'}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// SECTION 537: the awards the claim relies on - open, print, attach. The counter-authority is for the officer's eyes.
function AwardsPanel({ craft }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid ' + C.line, padding: '8px 12px', marginBottom: 10 }}>
      <button className="btn btn-secondary btn-sm" onClick={() => setOpen(!open)}>{open ? 'Hide the awards' : 'The awards this claim relies on - open, print, attach'}</button>
      {open ? (
        <div style={{ marginTop: 8, fontSize: 12, color: C.text }}>
          {SUPPORTING_AWARDS.map(a => (
            <div key={a.no} style={{ padding: '3px 0' }}>
              {wordsFor(craft).supportingAwardsLink && <a href={SUPPORTING_AWARDS_URL(a.no)} target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>Third Division Award {a.no}</a>}
              <span style={{ color: C.dim }}> - {a.held}</span>
            </div>
          ))}
          <div style={{ color: C.dim, marginTop: 6 }}>For you, not the claim: {SUPPORTING_AWARDS_NOTE}</div>
        </div>
      ) : null}
    </div>
  );
}

export default function ClaimFormPanel({ claim, user, claimId, onPatched, onModel }) {
  const committee = claimCommittee(claim);
  const [gcName, setGcName] = useState('');
  const [me, setMe] = useState(null);
  const [logo, setLogo] = useState(null);
  const [by, setBy] = useState(roleOf(user, claim));
  // Ruling 260: the claimant's recorded election decides the default; an officer may still set it for this print.
  const [claimantSigns, setClaimantSigns] = useState(!(claim && claim.claimantDeclinedSignature && claim.claimantDeclinedSignature.choice === 'declined'));
  const [profileTick, setProfileTick] = useState(0);
  const [url, setUrl] = useState('');
  const [svc, setSvc] = useState(null);   // section 539: serviceSettings/{committee}
  useEffect(() => {
    let dead = false;
    if (!committee) return undefined;
    getDoc(doc(db, 'serviceSettings', committee)).then(d => { if (!dead) setSvc(d.exists() ? d.data() : {}); }).catch(() => { if (!dead) setSvc({}); });
    return () => { dead = true; };
  }, [committee]);

  // Record 610 (ruling 259/262): the GC's and LC's directories, and the committee letterhead (the GC's office address).
  const [dirs, setDirs] = useState({ gc: null, lc: null, committeeDoc: null });
  useEffect(() => {
    let dead = false;
    loadDirectories(db, committee, claim && claim.scopeKey).then((d) => { if (!dead) setDirs(d); });
    return () => { dead = true; };
  }, [committee, claim && claim.scopeKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The General Chairperson of record (officers can read terms; a member may not - then the office alone prints).
  useEffect(() => {
    let dead = false;
    if (!committee) return undefined;
    getDocs(query(collection(db, 'officeTerms'), where('officeId', '==', committee)))
      .then(snap => {
        if (dead) return;
        const open = snap.docs.map(d => d.data()).filter(t => t && !t.endedOn && s(t.holderName));
        if (open.length === 1) setGcName(s(open[0].holderName));
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [committee]);

  // The signed-in person's own profile: the address that prints under an officer's signature (ruling 259).
  useEffect(() => {
    let dead = false;
    if (!user || !user.uid) return undefined;
    getDoc(doc(db, 'users', user.uid)).then(d => { if (!dead && d.exists()) setMe(d.data()); }).catch(() => {});
    return () => { dead = true; };
  }, [user && user.uid, profileTick]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let dead = false;
    fetch(LOGO.path)   // served from public\ at the site root
      .then(r => (r.ok ? r.arrayBuffer() : null))
      .then(b => { if (!dead && b) setLogo({ bytes: new Uint8Array(b), w: LOGO.w, h: LOGO.h }); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  // Record 610 (ruling 262): "Presented to" against the agreement and both directories - computed once, read by the
  // form (not-ready list / check list) and by the panel beside it.
  const ptcInputs = useMemo(() => {
    if (!claim) return null;
    const req = getClaimRequirements(claim.applicableAgreement);
    const slug = agreementSlug(claim.applicableAgreement);
    return { answer: claim.presentedTo, agreement: claim.applicableAgreement, designation: req && req.presentmentOfficer,
      gc: entryFor(dirs.gc, slug), lc: entryFor(dirs.lc, slug) };
  }, [claim, dirs]);
  const ptc = useMemo(() => (ptcInputs ? crossCheck({ ...ptcInputs, recorded: claim && claim.presentedToCheck }) : null), [ptcInputs, claim]);

  const model = useMemo(() => {
    if (!claim) return null;
    // Ruling 271 (2026-10-02): the body that stood here is penaltyClaim.js claimFormModel(), unchanged, so the copy the
    // filer is given at filing (NewClaim / QuickFile) is drawn by the same code as this form.
    return claimFormModel(claim, { committee, gcName, me, by, claimantSigns, user,
      requirements: getClaimRequirements(claim.applicableAgreement), resolveRule: resolveRuleText,
      service: serviceFor(claim.applicableAgreement, getClaimRequirements(claim.applicableAgreement), svc) || undefined,
      // Record 610: the seat's office address under an officer's signature (259); the three-way check of "Presented to" (262).
      officeAddress: officeAddressFor(by, { letterhead: dirs.committeeDoc && dirs.committeeDoc.letterhead, local: dirs.lc }),
      presentedToCheck: ptc || undefined });
  }, [claim, gcName, committee, me, by, claimantSigns, user, svc, dirs, ptc]);

  const [printNote, setPrintNote] = useState('');   // section 761 (ruling 325): what happened when the claim was printed
  // RULING 277: the claim check at the top of the page reads this same model - one list, not two.
  useEffect(() => { if (onModel) onModel(model || null); }, [model, onModel]);

  useEffect(() => {
    if (!model) return undefined;
    let u = '';
    try {
      const pdf = penaltyClaimPdf(model, { logo: logo || undefined, preview: !model.sendable, sendable: model.sendable });
      u = URL.createObjectURL(new Blob([pdf.bytes], { type: 'application/pdf' }));
      setUrl(u);
    } catch (e) { setUrl(''); }
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [model, logo]);

  if (!claim || !model) return null;
  const officerViewer = user && (user.isAdmin || user.role === 'local_chair' || user.role === 'general_chair');
  const file = claimFormFileName(claim, model);   // ruling 271: one naming rule, shared with the filer's copy
  // Ruling 325 (section 761): printing the READY form sends it to the member's LC and GC and records it. A PREVIEW is not a print.
  const recordPrint = () => {
    if (!model.sendable || !claimId) return;
    httpsCallable(getFunctions(), 'claimPrinted')({ claimId })
      .then((r) => setPrintNote((r && r.data && r.data.note) || 'Print recorded.'))
      .catch(() => setPrintNote('The print was not recorded, so your Local and General Chairperson were not told. Print it again to retry.'));
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div>
          <div className="card-title">The claim - Penalty Time Claim</div>
          <div className="card-subtitle">This is exactly what prints. Read it before anyone signs it. The claimant may print and serve it herself; the platform never signs it for anyone.</div>
        </div>
      </div>

      {model.signerFrozen ? (
        <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>
          Signed and served by {model.signer.by === 'claimant' ? 'the claimant' : (model.signer.name || 'the officer') + ', ' + (model.signer.title || '')}. The signature block is fixed as it was served.
        </div>
      ) : officerViewer && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: C.text, marginBottom: 10 }}>
          <span style={{ color: C.dim }}>Prepared for submission by:</span>
          {FORM_ROLES.filter(([k]) => formRolesFor(user).includes(k)).map(([k, l]) => (
            <label key={k} style={{ cursor: 'pointer' }}><input aria-label="Party" type="radio" checked={by === k} onChange={() => setBy(k)} /> {l}</label>
          ))}
          {by !== 'claimant' && (
            <label style={{ cursor: 'pointer', marginLeft: 8 }}>
              <input aria-label="The claimant will also sign (highly recommended)" type="checkbox" checked={claimantSigns} onChange={e => setClaimantSigns(e.target.checked)} /> The claimant will also sign (highly recommended)
            </label>
          )}
        </div>
      )}

      <ClaimFormActions claimId={claimId} claim={claim} user={user} model={model} filingBy={by}
        onPatched={(patch, info) => { if (info && info.profileChanged) setProfileTick(n => n + 1); if (onPatched && patch && Object.keys(patch).length) onPatched(patch); }} />

      <PresentedToCheckPanel claim={claim} claimId={claimId} user={user} result={ptc} inputs={ptcInputs} onPatched={onPatched} />   {/* Record 610 (ruling 262) */}
      {officerViewer && claim.status !== 'draft' ? <CompletionPanel claim={claim} claimId={claimId} user={user} missing={model.missing} onPatched={onPatched} /> : null}
      {officerViewer ? <AwardsPanel craft={claim && claim.craft} /> : null}
      <div style={{ fontSize: 12, padding: '8px 12px', border: '1px solid ' + (model.sendable ? C.ok : C.accent), color: model.sendable ? C.ok : C.accent, marginBottom: 10 }}>
        {model.sendable ? 'Everything the form needs is on the record. Sign it before you submit it.' : (
          <>NOT READY TO SEND - the copy below is marked PREVIEW. Still needed: {model.missing.join('; ')}.</>
        )}
        {model.check.length > 0 && (
          <div style={{ color: C.dim, marginTop: 6 }}>Check before signing: {model.check.join(' ')}</div>
        )}
      </div>

      {url ? (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <a className="btn btn-primary btn-sm" href={url} download={file} onClick={recordPrint}>Download PDF</a>
            <a className="btn btn-secondary btn-sm" href={url + '#zoom=125'} target="_blank" rel="noopener noreferrer" onClick={recordPrint}>Open full size to read or print</a>
            <SlidesButton label="▶ Present as slides" build={() => claimDeck(model, claim)} />   {/* Ruling 311: the claim form's own content */}
            <button className="btn btn-secondary btn-sm" onClick={() => {
              recordPrint();
              try {
                const bytes = penaltyClaimDocx(model, { logo: logo || undefined, preview: !model.sendable });
                const u = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
                const a = document.createElement('a'); a.href = u; a.download = file.replace(/\.pdf$/, '.docx');
                document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(u), 2000);
              } catch (e) { /* the PDF remains available */ }
            }}>Download Word</button>
            <span style={{ fontSize: 11, color: C.accent, alignSelf: 'center' }}>Sign it before you submit it. Save the signed copy to this claim's Documents.</span>
            {model.sendable ? <span style={{ fontSize: 11, color: C.dim, alignSelf: 'center' }}>Printing it sends it to your Local and General Chairperson.</span> : null}
            {printNote ? <span style={{ fontSize: 11, color: C.ok, alignSelf: 'center' }}>{printNote}</span> : null}
          </div>
          {/* Section 537 (Bob: "the preview is barely legible because of the small print"): open at the page's width, not the whole page shrunk to fit. */}
          <DocumentReview title="Penalty Time Claim" kind="claim" text={penaltyClaimText(model.form)} facts={claimFacts(claim)} record={claimRecordLine(claim)} today={new Date().toISOString().slice(0, 10)} />
          <iframe title="Penalty Time Claim" src={url + '#zoom=page-width&view=FitH'} style={{ width: '100%', height: 1100, border: '1px solid ' + C.line, background: '#fff' }} />
        </>
      ) : <div style={{ fontSize: 12, color: C.bad }}>The form could not be drawn for this claim.</div>}
    </div>
  );
}
